'use client'
import React, { DragEvent, useRef, useState } from 'react'
import { Edit, EyeFilled, ReorderIcon, SaveDisk, Trash } from '../icons'
import clsx from 'clsx'

export interface FileData {
  id?: number
  url: string
  fileName: string
  platformType?: string
  fileType?: string
  properties?: any
  order: number
}

export interface FileUploadSectionProps {
  files: FileData[]
  onChange: (files: FileData[]) => void
  onUpload: (file: File) => Promise<FileData | null>
  isMutating?: boolean
  maxFileSizeMB?: number
  allowedExtensions?: string[]
  title?: string
  subtitle?: string
  dragDropText?: string
  supportText?: string
  className?: string
}

export function FileUploadSection({
  files,
  onChange,
  onUpload,
  isMutating = false,
  maxFileSizeMB = 10,
  allowedExtensions = [
    'pdf',
    'doc',
    'docx',
    'txt',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
  ],
  title = 'Archivos adjuntos',
  subtitle = '',
  dragDropText = 'Seleccionar archivos múltiples',
  supportText = '',
  className = '',
}: FileUploadSectionProps) {
  const [editingFile, setEditingFile] = useState<number | null>(null)
  const [editingFileName, setEditingFileName] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const getFileTypeIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || ''))
      return '🖼️'
    if (['pdf'].includes(extension || '')) return '📄'
    if (['doc', 'docx'].includes(extension || '')) return '📝'
    if (['xls', 'xlsx'].includes(extension || '')) return '📊'
    if (['ppt', 'pptx'].includes(extension || '')) return '📊'
    return '📁'
  }

  const formatSupportText = () => {
    if (supportText) return supportText

    // Agrupar extensiones por tipo
    const imageExts = allowedExtensions.filter((ext) =>
      ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    )
    const docExts = allowedExtensions.filter((ext) =>
      ['pdf', 'doc', 'docx', 'txt'].includes(ext)
    )
    const spreadsheetExts = allowedExtensions.filter((ext) =>
      ['xls', 'xlsx'].includes(ext)
    )
    const presentationExts = allowedExtensions.filter((ext) =>
      ['ppt', 'pptx'].includes(ext)
    )

    const groups: string[] = []
    if (imageExts.length > 0) groups.push('imágenes')
    if (docExts.length > 0) groups.push('documentos')
    if (spreadsheetExts.length > 0) groups.push('hojas de cálculo')
    if (presentationExts.length > 0) groups.push('presentaciones')

    return `Soporta ${groups.join(', ')} y más`
  }

  const handleFiles = async (newFiles: File[]) => {
    setIsUploading(true)
    try {
      const uploadPromises = newFiles.map((file) => onUpload(file))
      const uploadedFiles = (await Promise.all(uploadPromises)).filter(
        Boolean
      ) as FileData[]

      if (uploadedFiles.length > 0) {
        onChange([...files, ...uploadedFiles])
      }
    } catch (error) {
      console.error('Error handling files:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      await handleFiles(droppedFiles)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length > 0) {
      await handleFiles(selectedFiles)
    }
    e.target.value = ''
  }

  const handleMultipleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    const reorderedFiles = newFiles.map((file, i) => ({
      ...file,
      order: i + 1,
    }))
    onChange(reorderedFiles)
  }

  const handleEditFileName = (index: number) => {
    setEditingFile(index)
    setEditingFileName(files[index].fileName)
  }

  const handleSaveFileName = (index: number) => {
    const newFiles = [...files]
    newFiles[index] = { ...newFiles[index], fileName: editingFileName }
    onChange(newFiles)
    setEditingFile(null)
    setEditingFileName('')
  }

  const handleFileDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleFileDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) return

    const newFiles = [...files]
    const draggedFile = newFiles[draggedIndex]
    newFiles.splice(draggedIndex, 1)
    newFiles.splice(dropIndex, 0, draggedFile)
    const reorderedFiles = newFiles.map((file, index) => ({
      ...file,
      order: index + 1,
    }))

    onChange(reorderedFiles)
    setDraggedIndex(null)
  }

  return (
    <div className={clsx('space-y-6', className)}>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>

      <div className="space-y-4">
        {/* Upload Area */}
        <div
          className={clsx(
            'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            isDragOver ? 'border-admin-blue bg-blue-50' : 'border-gray-300',
            isUploading && 'pointer-events-none opacity-50'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleMultipleFileSelect}>
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48">
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <span className="font-medium text-admin-blue hover:text-admin-blue/80">
                {isUploading ? 'Subiendo archivos...' : dragDropText}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Arrastra y suelta archivos aquí o haz clic para seleccionar
            </p>
            <p className="text-xs text-gray-500">{formatSupportText()}</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
            disabled={isMutating || isUploading}
          />
        </div>

        {/* Lista de archivos */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">
                Archivos subidos:
              </h4>
              <p className="text-xs text-gray-500">Arrastra para reordenar</p>
            </div>
            <div className="space-y-2">
              {files.map((file, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleFileDragStart(e, index)}
                  onDragOver={handleFileDragOver}
                  onDrop={(e) => handleFileDrop(e, index)}
                  className={clsx(
                    'flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3',
                    draggedIndex === index && 'opacity-50'
                  )}>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                      disabled={isMutating}>
                      <ReorderIcon className="h-3.5 w-3.5" />
                    </button>
                    <span className="text-lg">
                      {getFileTypeIcon(file.fileName)}
                    </span>
                    {editingFile === index ? (
                      <input
                        type="text"
                        value={editingFileName}
                        onChange={(e) => setEditingFileName(e.target.value)}
                        className="w-full flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveFileName(index)
                          } else if (e.key === 'Escape') {
                            setEditingFile(null)
                            setEditingFileName('')
                          }
                        }}
                      />
                    ) : (
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {file.fileName}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {editingFile === index ? (
                      <button
                        type="button"
                        onClick={() => handleSaveFileName(index)}
                        className="inline-flex items-center rounded-full p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-admin-blue"
                        title="Guardar nombre">
                        <SaveDisk className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleEditFileName(index)}
                        disabled={isMutating}
                        className="inline-flex items-center rounded-full p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-admin-blue"
                        title="Renombrar">
                        <Edit className="h-4 w-4" />
                      </button>
                    )}

                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-blue-600 hover:text-blue-800">
                      <EyeFilled className="h-4 w-4 fill-[#5C5C5C]" />
                    </a>

                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="inline-flex items-center rounded-full p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                      title="Eliminar archivo">
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
